import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { FiMaximize, FiTrash2 } from 'react-icons/fi';
import { Icon } from '@iconify/react';
import Confetti from 'react-confetti';
import 'flag-icons/css/flag-icons.min.css';

// Import the images properly
import champonImage from '../assets/champon.png';
import catGif from '../assets/cat.gif';

const Container = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  overflow: hidden;
  padding: 5px;
`;

const ScaledWrapper = styled.div`
  width: 1366px;
  height: 598px;
  transform-origin: center center;
  position: relative;
`;

const ContentWrapper = styled.div`
  background: white;
  border-radius: 24px;
  padding: 20px 30px;
  width: 100%;
  height: 100%;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
`;

const Title = styled(motion.h1)`
  text-align: center;
  color: #334155;
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 15px 0;
  flex-shrink: 0;
`;

const MainContent = styled.div`
  display: flex;
  gap: 40px;
  flex: 1;
  min-height: 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const IngredientsSection = styled.div`
  flex: 1.5;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const IngredientsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  flex: 1;
`;

const IngredientItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 18px 20px;
  background: #f8fafc;
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const IngredientLetter = styled.div`
  width: 85px;
  height: 85px;
  background: transparent;
  border: 5px solid #3b82f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-weight: 900;
  font-size: 42px;
  flex-shrink: 0;
`;

const IngredientDetails = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const IngredientName = styled.div`
  font-weight: 700;
  color: #334155;
  font-size: 34px;
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const IngredientEmoji = styled.span`
  font-size: 38px;
  flex-shrink: 0;
`;

const IngredientCountry = styled.div`
  font-size: 34px;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
`;

const CountryFlag = styled.span`
  width: 40px;
  height: 30px;
  border-radius: 4px;
  overflow: hidden;
  display: inline-block;
  
  &.fi {
    background-size: cover;
    background-position: center;
  }
`;

const ChamponSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
`;

const ChamponBowl = styled(motion.div)`
  width: 500px;
  height: 455px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  margin-left: 35px;
  margin-bottom: 40px;
`;

const ChamponImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const ChamponSVG = styled.svg`
  width: 100%;
  height: 100%;
  max-width: 500px;
  max-height: 450px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  
  .floating-ingredient {
    user-select: none;
    transform-origin: center center;
    will-change: transform, clip-path;
    backface-visibility: hidden;
    perspective: 1000px;
  }
  
  foreignObject {
    overflow: visible;
  }
  
  foreignObject > div {
    will-change: transform;
    backface-visibility: hidden;
    transform-style: preserve-3d;
  }
  
  .ingredient-1 { 
    animation: float1 4s ease-in-out infinite; 
  }
  .ingredient-1.surface { 
    animation: float1 4s ease-in-out infinite, surface1 4s ease-in-out infinite; 
  }
  .ingredient-1.submerged { 
    animation: float1 4s ease-in-out infinite, submerged1 4s ease-in-out infinite; 
  }
  
  .ingredient-2 { 
    animation: float2 3.5s ease-in-out infinite 0.5s; 
  }
  .ingredient-2.surface { 
    animation: float2 3.5s ease-in-out infinite 0.5s, surface2 3.5s ease-in-out infinite 0.5s; 
  }
  .ingredient-2.submerged { 
    animation: float2 3.5s ease-in-out infinite 0.5s, submerged2 3.5s ease-in-out infinite 0.5s; 
  }
  
  .ingredient-3 { 
    animation: float3 4.5s ease-in-out infinite 1s; 
  }
  .ingredient-3.surface { 
    animation: float3 4.5s ease-in-out infinite 1s, surface3 4.5s ease-in-out infinite 1s; 
  }
  .ingredient-3.submerged { 
    animation: float3 4.5s ease-in-out infinite 1s, submerged3 4.5s ease-in-out infinite 1s; 
  }
  
  .ingredient-4 { 
    animation: float4 3.8s ease-in-out infinite 1.5s; 
  }
  .ingredient-4.surface { 
    animation: float4 3.8s ease-in-out infinite 1.5s, surface4 3.8s ease-in-out infinite 1.5s; 
  }
  .ingredient-4.submerged { 
    animation: float4 3.8s ease-in-out infinite 1.5s, submerged4 3.8s ease-in-out infinite 1.5s; 
  }
  
  .ingredient-5 { 
    animation: float5 4.2s ease-in-out infinite 2s; 
  }
  .ingredient-5.surface { 
    animation: float5 4.2s ease-in-out infinite 2s, surface5 4.2s ease-in-out infinite 2s; 
  }
  .ingredient-5.submerged { 
    animation: float5 4.2s ease-in-out infinite 2s, submerged5 4.2s ease-in-out infinite 2s; 
  }
  
  .ingredient-6 { 
    animation: float6 3.7s ease-in-out infinite 0.8s; 
  }
  .ingredient-6.surface { 
    animation: float6 3.7s ease-in-out infinite 0.8s, surface6 3.7s ease-in-out infinite 0.8s; 
  }
  .ingredient-6.submerged { 
    animation: float6 3.7s ease-in-out infinite 0.8s, submerged6 3.7s ease-in-out infinite 0.8s; 
  }
  .ingredient-3 { animation: float3 4.5s ease-in-out infinite 1s; }
  .ingredient-4 { animation: float4 3.8s ease-in-out infinite 1.5s; }
  .ingredient-5 { animation: float5 4.2s ease-in-out infinite 2s; }
  .ingredient-6 { animation: float6 3.7s ease-in-out infinite 0.8s; }
  
  @keyframes float1 {
    0%, 100% { transform: translate3d(0, 0, 0) rotate(-3deg); }
    25% { transform: translate3d(0, -8px, 0) rotate(2deg); }
    50% { transform: translate3d(0, -15px, 0) rotate(-1deg); }
    75% { transform: translate3d(0, -8px, 0) rotate(3deg); }
  }
  
  @keyframes surface1 {
    0%, 100% { clip-path: polygon(0 0, 100% 0, 100% 50%, 0 50%); }
    25% { clip-path: polygon(0 0, 100% 0, 100% 58%, 0 58%); }
    50% { clip-path: polygon(0 0, 100% 0, 100% 65%, 0 65%); }
    75% { clip-path: polygon(0 0, 100% 0, 100% 58%, 0 58%); }
  }
  
  @keyframes submerged1 {
    0%, 100% { clip-path: polygon(0 50%, 100% 50%, 100% 100%, 0 100%); }
    25% { clip-path: polygon(0 58%, 100% 58%, 100% 100%, 0 100%); }
    50% { clip-path: polygon(0 65%, 100% 65%, 100% 100%, 0 100%); }
    75% { clip-path: polygon(0 58%, 100% 58%, 100% 100%, 0 100%); }
  }
  
  @keyframes float2 {
    0%, 100% { transform: translate3d(0, 0, 0) rotate(2deg); }
    30% { transform: translate3d(0, -12px, 0) rotate(-2deg); }
    60% { transform: translate3d(0, -18px, 0) rotate(1deg); }
  }
  
  @keyframes surface2 {
    0%, 100% { clip-path: polygon(0 0, 100% 0, 100% 50%, 0 50%); }
    30% { clip-path: polygon(0 0, 100% 0, 100% 62%, 0 62%); }
    60% { clip-path: polygon(0 0, 100% 0, 100% 68%, 0 68%); }
  }
  
  @keyframes submerged2 {
    0%, 100% { clip-path: polygon(0 50%, 100% 50%, 100% 100%, 0 100%); }
    30% { clip-path: polygon(0 62%, 100% 62%, 100% 100%, 0 100%); }
    60% { clip-path: polygon(0 68%, 100% 68%, 100% 100%, 0 100%); }
  }
  
  @keyframes float3 {
    0%, 100% { transform: translate3d(0, 0, 0) rotate(-2deg); }
    40% { transform: translate3d(0, -10px, 0) rotate(3deg); }
    80% { transform: translate3d(0, -16px, 0) rotate(-1deg); }
  }
  
  @keyframes surface3 {
    0%, 100% { clip-path: polygon(0 0, 100% 0, 100% 50%, 0 50%); }
    40% { clip-path: polygon(0 0, 100% 0, 100% 60%, 0 60%); }
    80% { clip-path: polygon(0 0, 100% 0, 100% 66%, 0 66%); }
  }
  
  @keyframes submerged3 {
    0%, 100% { clip-path: polygon(0 50%, 100% 50%, 100% 100%, 0 100%); }
    40% { clip-path: polygon(0 60%, 100% 60%, 100% 100%, 0 100%); }
    80% { clip-path: polygon(0 66%, 100% 66%, 100% 100%, 0 100%); }
  }
  
  @keyframes float4 {
    0%, 100% { transform: translate3d(0, 0, 0) rotate(1deg); }
    35% { transform: translate3d(0, -14px, 0) rotate(-3deg); }
    70% { transform: translate3d(0, -9px, 0) rotate(2deg); }
  }
  
  @keyframes surface4 {
    0%, 100% { clip-path: polygon(0 0, 100% 0, 100% 50%, 0 50%); }
    35% { clip-path: polygon(0 0, 100% 0, 100% 64%, 0 64%); }
    70% { clip-path: polygon(0 0, 100% 0, 100% 59%, 0 59%); }
  }
  
  @keyframes submerged4 {
    0%, 100% { clip-path: polygon(0 50%, 100% 50%, 100% 100%, 0 100%); }
    35% { clip-path: polygon(0 64%, 100% 64%, 100% 100%, 0 100%); }
    70% { clip-path: polygon(0 59%, 100% 59%, 100% 100%, 0 100%); }
  }
  
  @keyframes float5 {
    0%, 100% { transform: translate3d(0, 0, 0) rotate(-1deg); }
    25% { transform: translate3d(0, -11px, 0) rotate(2deg); }
    75% { transform: translate3d(0, -17px, 0) rotate(-2deg); }
  }
  
  @keyframes surface5 {
    0%, 100% { clip-path: polygon(0 0, 100% 0, 100% 50%, 0 50%); }
    25% { clip-path: polygon(0 0, 100% 0, 100% 61%, 0 61%); }
    75% { clip-path: polygon(0 0, 100% 0, 100% 67%, 0 67%); }
  }
  
  @keyframes submerged5 {
    0%, 100% { clip-path: polygon(0 50%, 100% 50%, 100% 100%, 0 100%); }
    25% { clip-path: polygon(0 61%, 100% 61%, 100% 100%, 0 100%); }
    75% { clip-path: polygon(0 67%, 100% 67%, 100% 100%, 0 100%); }
  }
  
  @keyframes float6 {
    0%, 100% { transform: translate3d(0, 0, 0) rotate(3deg); }
    45% { transform: translate3d(0, -13px, 0) rotate(-1deg); }
    85% { transform: translate3d(0, -8px, 0) rotate(2deg); }
  }
  
  @keyframes surface6 {
    0%, 100% { clip-path: polygon(0 0, 100% 0, 100% 50%, 0 50%); }
    45% { clip-path: polygon(0 0, 100% 0, 100% 63%, 0 63%); }
    85% { clip-path: polygon(0 0, 100% 0, 100% 58%, 0 58%); }
  }
  
  @keyframes submerged6 {
    0%, 100% { clip-path: polygon(0 50%, 100% 50%, 100% 100%, 0 100%); }
    45% { clip-path: polygon(0 63%, 100% 63%, 100% 100%, 0 100%); }
    85% { clip-path: polygon(0 58%, 100% 58%, 100% 100%, 0 100%); }
  }
`;

const FullscreenButton = styled.button`
  position: absolute;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #334155;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  background: rgba(239, 68, 68, 0.9);
  border: none;
  border-radius: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  &:hover {
    background: rgba(220, 38, 38, 1);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }
`;

const ActionButton = styled.button`
  position: absolute;
  top: 30px;
  ${props => props.position === 'left' ? 'left' : 'right'}: 30px;
  padding: 15px 30px;
  background: rgba(59, 130, 246, 0.9);
  border: none;
  border-radius: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: white;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  &:hover {
    background: rgba(37, 99, 235, 1);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }
`;

const LoadingScreen = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  overflow: hidden;
`;

const LoadingGif = styled.img`
  width: 250px;
  height: 250px;
  margin-bottom: 40px;
  z-index: 3001;
`;

const LoadingText = styled(motion.div)`
  font-size: 48px;
  font-weight: 700;
  color: #334155;
  text-align: center;
  z-index: 3001;
  
  .dots::after {
    content: '';
    animation: ellipsis 1.5s infinite;
  }
  
  @keyframes ellipsis {
    0% { content: ''; }
    25% { content: '.'; }
    50% { content: '..'; }
    75% { content: '...'; }
    100% { content: ''; }
  }
`;

const FlyingIngredient = styled(motion.div)`
  position: absolute;
  font-size: 96px;
  z-index: 3000;
  user-select: none;
  pointer-events: none;
  transform-origin: center center;
  left: 0;
  top: 0;
`;

function ChamponPresentation({ ingredients, onNewChampon, width, height }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [placedIngredients, setPlacedIngredients] = useState([]);
  const [ingredientIcons, setIngredientIcons] = useState([]);
  const [showLoading, setShowLoading] = useState(true);
  const [flyingIngredients, setFlyingIngredients] = useState([]);
  const [scale, setScale] = useState(1);

  // Calculate scale based on viewport size - TRUE uniform scaling like a video game
  useEffect(() => {
    const calculateScale = () => {
      const viewportWidth = width || window.innerWidth;
      const viewportHeight = height || window.innerHeight;
      
      // Base dimensions for the design (1366x598 - matches target screen area)
      const baseWidth = 1366;
      const baseHeight = 598;
      
      // Account for container padding (5px on each side = 10px total)
      const availableWidth = viewportWidth - 10;
      const availableHeight = viewportHeight - 10;
      
      // Calculate scale to fit viewport while maintaining EXACT aspect ratio
      const scaleX = availableWidth / baseWidth;
      const scaleY = availableHeight / baseHeight;
      const newScale = Math.min(scaleX, scaleY); // No artificial limiting
      
      setScale(newScale);
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [width, height]);

  // Show loading screen for 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Create flying ingredients animation
  useEffect(() => {
    if (showLoading && ingredients.length > 0) {
      const createFlyingIngredient = (ingredient, delay, index, totalIngredients) => {
        const id = Math.random().toString(36).substr(2, 9);
        const screenWidth = width || window.innerWidth || 1200;
        
        // Random distribution across full screen width with margins
        const margin = 50;
        const availableWidth = screenWidth - (2 * margin);
        const startX = margin + Math.random() * availableWidth;
        
        const peakHeight = 400 + Math.random() * 400; // 400-800px
        const horizontalDrift = (Math.random() - 0.5) * 150; // Small drift
        const rotationSpeed = (Math.random() - 0.5) * 360; // Reduced rotation for performance


        return {
          id,
          icon: ingredient.icon,
          iconType: ingredient.iconType,
          startX,
          peakHeight,
          horizontalDrift,
          rotationSpeed,
          delay
        };
      };

      // Create multiple waves of ingredients (reduced from 3 to 2 for performance)
      const waves = [];
      for (let wave = 0; wave < 2; wave++) {
        ingredients.forEach((ingredient, index) => {
          const delay = wave * 1200 + index * 300 + Math.random() * 200; // Spread out timing
          waves.push(createFlyingIngredient(ingredient, delay, index, ingredients.length));
        });
      }

      setFlyingIngredients(waves);
    }
  }, [showLoading, ingredients, width, height]);

  // Bowl parameters for positioning ingredients
  const bowlCenterX = 571;
  const bowlCenterY = 880 - 612;
  const bowlRadiusX = 440;
  const bowlRadiusY = 225;

  const getRandomPosition = () => {
    let angle = Math.random() * 2 * Math.PI;
    let r = Math.sqrt(Math.random()) * 0.8;

    return {
      x: bowlCenterX + r * bowlRadiusX * Math.cos(angle),
      y: bowlCenterY + r * bowlRadiusY * Math.sin(angle)
    };
  };

  const findNonOverlappingPosition = (size, currentPlaced) => {
    let attempts = 0;
    let position;
    do {
      position = getRandomPosition();
      attempts++;
    } while (checkCollision(position.x, position.y, size, currentPlaced) && attempts < 100);

    return attempts < 100 ? position : null;
  };

  const checkCollision = (x, y, size, placedList) => {
    for (let placed of placedList) {
      const dx = x - placed.x;
      const dy = y - placed.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < size + placed.size) {
        return true;
      }
    }
    return false;
  };

  const getIngredientStyle = (icon) => {
    // No static styles here - we'll handle submersion with SVG masks
    return {};
  };

  // Remove the complex keyframe generation function
  useEffect(() => {
    const positionIngredients = () => {
      const newPlacedIngredients = [];
      const newIngredientIcons = [];

      ingredients.forEach((ingredient, index) => {
        const iconSize = 200; // Increased from 160 to 200
        const position = findNonOverlappingPosition(iconSize / 2, newPlacedIngredients);

        if (position) {
          const { x, y } = position;
          const rotation = Math.random() * 360;
          const halfSize = iconSize / 2;

          newPlacedIngredients.push({ 
            x: position.x, 
            y: position.y, 
            size: iconSize / 2 
          });

          newIngredientIcons.push({
            id: ingredient.id,
            icon: ingredient.icon,
            iconType: ingredient.iconType,
            x: x - halfSize,
            y: y - halfSize,
            size: iconSize,
            rotation: rotation,
            centerX: x,
            centerY: y,
            baseY: y, // Store base Y position
            waterLevel: y + halfSize // Water level at ingredient center
          });
        }
      });

      setPlacedIngredients(newPlacedIngredients);
      setIngredientIcons(newIngredientIcons);
    };

    if (ingredients.length > 0) {
      positionIngredients();
    }
  }, [ingredients]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  return (
    <>
      <AnimatePresence>
        {showLoading && (
          <LoadingScreen
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Flying ingredients */}
            {flyingIngredients.map((flyingIngredient) => {
              const finalX = Math.max(25, Math.min((width || window.innerWidth) - 75, flyingIngredient.startX + flyingIngredient.horizontalDrift));
              
              return (
                <FlyingIngredient
                  key={flyingIngredient.id}
                  style={{ 
                    left: `${flyingIngredient.startX}px`,
                    top: `${(height || window.innerHeight) + 100}px`
                  }}
                  animate={{
                    left: `${finalX}px`,
                    top: [
                      `${(height || window.innerHeight) + 100}px`,
                      `${(height || window.innerHeight) - flyingIngredient.peakHeight}px`,
                      `${(height || window.innerHeight) + 100}px`
                    ],
                    rotate: flyingIngredient.rotationSpeed,
                    scale: [1, 1.1, 1] // Reduced scale change for performance
                  }}
                  transition={{
                    duration: 2.5, // Slightly longer duration for smoother animation
                    delay: flyingIngredient.delay / 1000,
                    ease: "easeOut", // Simpler easing for better performance
                    times: [0, 0.4, 1] // Adjusted timing for more natural arc
                  }}
                >
                  {flyingIngredient.iconType === 'image' ? (
                    <img 
                      src={flyingIngredient.icon} 
                      alt="Flying ingredient"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <Icon icon={flyingIngredient.icon} style={{ width: '100%', height: '100%' }} />
                  )}
                </FlyingIngredient>
              );
            })}
            
            <LoadingGif src={catGif} alt="Loading cat" />
            <LoadingText
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Making Champon<span className="dots"></span>
            </LoadingText>
          </LoadingScreen>
        )}
      </AnimatePresence>

      {!showLoading && (
        <Container
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ScaledWrapper style={{ transform: `scale(${scale})` }}>
            <ContentWrapper>
        <Title
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          🍜 My Champon!
        </Title>

        <MainContent>
          <IngredientsSection>
            <IngredientsList>
              <AnimatePresence>
                {ingredients.map((ingredient, index) => (
                  <IngredientItem
                    key={ingredient.id}
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                  >
                    <IngredientLetter>{ingredient.letter}</IngredientLetter>
                    <IngredientDetails>
                      <IngredientName>
                        <IngredientEmoji>
                          {ingredient.iconType === 'image' ? (
                            <img 
                              src={ingredient.icon} 
                              alt={ingredient.name}
                              style={{
                                width: '1em',
                                height: '1em',
                                objectFit: 'contain'
                              }}
                            />
                          ) : (
                            <Icon icon={ingredient.icon} width="1em" height="1em" />
                          )}
                        </IngredientEmoji>
                        {ingredient.name}
                      </IngredientName>
                      <IngredientCountry>
                        from {ingredient.country.name}
                      </IngredientCountry>
                    </IngredientDetails>
                  </IngredientItem>
                ))}
              </AnimatePresence>
            </IngredientsList>
          </IngredientsSection>

          <ChamponSection>
            <ChamponBowl
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
            >
              <ChamponImage src={champonImage} alt="Champon Bowl" />
              <ChamponSVG viewBox="0 0 1142 880">
                <defs>
                  {ingredientIcons.map((icon, index) => (
                    <React.Fragment key={`defs-${icon.id}`}>
                      {/* Gradient mask for transparency effect */}
                      <linearGradient id={`transparency-gradient-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: 'white', stopOpacity: 1 }} />
                        <stop offset="50%" style={{ stopColor: 'white', stopOpacity: 1 }} />
                        <stop offset="75%" style={{ stopColor: 'white', stopOpacity: 0.6 }} />
                        <stop offset="100%" style={{ stopColor: 'white', stopOpacity: 0.2 }} />
                      </linearGradient>
                      
                      <mask id={`transparency-mask-${index}`}>
                        <rect 
                          x={icon.x} 
                          y={icon.y} 
                          width={icon.size} 
                          height={icon.size}
                          fill={`url(#transparency-gradient-${index})`}
                          className={`floating-ingredient ingredient-${index + 1}`}
                        />
                      </mask>
                    </React.Fragment>
                  ))}
                </defs>
                
                {ingredientIcons.map((icon, index) => (
                  <g key={icon.id}>
                    {/* Top half - surface portion with dynamic water line */}
                    <foreignObject
                      x={icon.x}
                      y={icon.y}
                      width={icon.size}
                      height={icon.size}
                      className={`floating-ingredient ingredient-${index + 1} surface`}
                      style={getIngredientStyle(icon)}
                    >
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: `${icon.size * 0.8}px`,
                        transform: 'translateZ(0)',
                        backfaceVisibility: 'hidden'
                      }}>
                        {icon.iconType === 'image' ? (
                          <img 
                            src={icon.icon} 
                            alt="Floating ingredient"
                            style={{
                              width: '80%',
                              height: '80%',
                              objectFit: 'contain',
                              transform: 'translateZ(0)',
                              backfaceVisibility: 'hidden'
                            }}
                          />
                        ) : (
                          <Icon 
                            icon={icon.icon} 
                            style={{
                              width: '80%', 
                              height: '80%',
                              transform: 'translateZ(0)',
                              backfaceVisibility: 'hidden'
                            }} 
                          />
                        )}
                      </div>
                    </foreignObject>
                    
                    {/* Bottom half - submerged portion with dynamic water line and transparency */}
                    <foreignObject
                      x={icon.x}
                      y={icon.y}
                      width={icon.size}
                      height={icon.size}
                      className={`floating-ingredient ingredient-${index + 1} submerged`}
                      mask={`url(#transparency-mask-${index})`}
                      style={{
                        filter: 'sepia(1) saturate(0.8) hue-rotate(30deg) brightness(0.9)'
                      }}
                    >
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: `${icon.size * 0.8}px`,
                        transform: 'translateZ(0)',
                        backfaceVisibility: 'hidden'
                      }}>
                        {icon.iconType === 'image' ? (
                          <img 
                            src={icon.icon} 
                            alt="Floating ingredient"
                            style={{
                              width: '80%',
                              height: '80%',
                              objectFit: 'contain',
                              transform: 'translateZ(0)',
                              backfaceVisibility: 'hidden'
                            }}
                          />
                        ) : (
                          <Icon 
                            icon={icon.icon} 
                            style={{
                              width: '80%', 
                              height: '80%',
                              transform: 'translateZ(0)',
                              backfaceVisibility: 'hidden'
                            }} 
                          />
                        )}
                      </div>
                    </foreignObject>
                  </g>
                ))}
              </ChamponSVG>
            </ChamponBowl>
          </ChamponSection>
        </MainContent>

        <FullscreenButton onClick={toggleFullscreen}>
          <FiMaximize />
        </FullscreenButton>

        <ActionButton position="right" variant="new" onClick={onNewChampon}>
          <FiTrash2 />
          New Champon
        </ActionButton>
      </ContentWrapper>
          </ScaledWrapper>
    </Container>
      )}
    </>
  );
}

export default ChamponPresentation;